type DeviceType = 'mobile' | 'desktop';

type JsonRecord = Record<string, unknown>;

const OTP_FUNNEL_INSTANCE_STORAGE_KEY = 'otokoc_otp_funnel_instance_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

let otpFunnelInstanceId: string | undefined;
let fallbackUuidCounter = 0;

export type OtokocDeviceContext = {
  deviceType: DeviceType;
  isMobile: boolean;
  windowWidth?: number;
  windowHeight?: number;
};

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function resolveOtokocDeviceContext(): OtokocDeviceContext {
  const width = typeof window.innerWidth === 'number' ? window.innerWidth : undefined;
  const height = typeof window.innerHeight === 'number' ? window.innerHeight : undefined;
  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true;
  const isMobile = (typeof width === 'number' && width <= 768) || hasCoarsePointer;

  return {
    deviceType: isMobile ? 'mobile' : 'desktop',
    isMobile,
    windowWidth: width,
    windowHeight: height,
  };
}

function formatUuid(bytes: Uint8Array): string {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

export function createCryptographicBrowserUuid(): string | undefined {
  const cryptoApi = globalThis.crypto;
  try {
    const randomUuid = cryptoApi?.randomUUID?.().toLowerCase();
    if (randomUuid && UUID_RE.test(randomUuid)) {
      return randomUuid;
    }
  } catch {
    // Some embedded browsers expose randomUUID but reject the call. Fall back
    // to getRandomValues before accepting the legacy unscoped contract.
  }

  try {
    if (!cryptoApi?.getRandomValues) {
      return undefined;
    }
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return formatUuid(bytes);
  } catch {
    return undefined;
  }
}

export function createBestEffortBrowserUuid(): string {
  const secureUuid = createCryptographicBrowserUuid();
  if (secureUuid) {
    return secureUuid;
  }

  // Session/action/event ids are correlation and dedupe labels, not secrets.
  // Preserve the UUID wire shape in legacy embedded browsers without using
  // this weaker fallback for the security-sensitive OTP funnel instance.
  fallbackUuidCounter = (fallbackUuidCounter + 1) >>> 0;
  const bytes = new Uint8Array(16);
  const now = Date.now();
  for (let index = 0; index < bytes.length; index += 1) {
    const timestampByte = Math.floor(now / 2 ** ((index % 6) * 8)) & 0xff;
    const counterByte = (fallbackUuidCounter >>> ((index % 4) * 8)) & 0xff;
    bytes[index] = timestampByte ^ counterByte ^ Math.floor(Math.random() * 256);
  }
  return formatUuid(bytes);
}

function persistOtpFunnelInstanceId(value: string): void {
  try {
    window.sessionStorage.setItem(OTP_FUNNEL_INSTANCE_STORAGE_KEY, value);
  } catch {
    // Storage is optional. The in-memory value still scopes this page lifetime.
  }
}

export function getOtpFunnelInstanceId(): string | undefined {
  if (otpFunnelInstanceId) {
    return otpFunnelInstanceId;
  }

  try {
    const stored = window.sessionStorage.getItem(OTP_FUNNEL_INSTANCE_STORAGE_KEY)?.trim();
    if (stored && UUID_RE.test(stored)) {
      otpFunnelInstanceId = stored.toLowerCase();
      return otpFunnelInstanceId;
    }
  } catch {
    // Fall through to a memory-only instance.
  }

  otpFunnelInstanceId = createCryptographicBrowserUuid();
  if (otpFunnelInstanceId) {
    persistOtpFunnelInstanceId(otpFunnelInstanceId);
  }
  return otpFunnelInstanceId;
}

export function rotateOtpFunnelInstanceId(): string | undefined {
  const nextInstanceId = createCryptographicBrowserUuid();
  if (!nextInstanceId) {
    // Never erase the current identity when browser entropy is unavailable.
    return otpFunnelInstanceId;
  }
  otpFunnelInstanceId = nextInstanceId;
  persistOtpFunnelInstanceId(nextInstanceId);
  return nextInstanceId;
}

export function resetOtpFunnelInstanceIdForTests(): void {
  otpFunnelInstanceId = undefined;
  try {
    window.sessionStorage.removeItem(OTP_FUNNEL_INSTANCE_STORAGE_KEY);
  } catch {
    // Tests may intentionally omit or reject browser storage.
  }
}

export function withOtokocDeviceMetadata(payload: unknown): unknown {
  if (!isRecord(payload)) {
    return payload;
  }

  const instanceId = getOtpFunnelInstanceId();
  return {
    ...payload,
    meta: {
      ...(isRecord(payload.meta) ? payload.meta : {}),
      ...resolveOtokocDeviceContext(),
      ...(instanceId ? { otpFunnelInstanceId: instanceId } : {}),
    },
  };
}

function resolveRequestUrl(input: RequestInfo | URL): string | null {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.url;
  }

  return null;
}

function resolveRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method;
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method;
  }

  return 'GET';
}

function isChatProcessActionRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  middlewareUrl: string
): boolean {
  if (resolveRequestMethod(input, init).toUpperCase() !== 'POST') {
    return false;
  }

  const rawUrl = resolveRequestUrl(input);
  if (!rawUrl) {
    return false;
  }

  try {
    const requestUrl = new URL(rawUrl, window.location.href);
    const endpointUrl = new URL(
      `${middlewareUrl.replace(/\/+$/, '')}/chat/process_action`,
      window.location.href
    );

    return requestUrl.origin === endpointUrl.origin && requestUrl.pathname === endpointUrl.pathname;
  } catch {
    return false;
  }
}

function enrichJsonBody(body: unknown): BodyInit | null {
  if (typeof body !== 'string') {
    return null;
  }

  try {
    return JSON.stringify(withOtokocDeviceMetadata(JSON.parse(body)));
  } catch {
    return null;
  }
}

export function mountChatDeviceMetadata(middlewareUrl: string): () => void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return () => {};
  }

  const previousFetch = window.fetch.bind(window);
  let active = true;

  const fetchWithDeviceMetadata: typeof window.fetch = async (input, init) => {
    if (!active || !isChatProcessActionRequest(input, init, middlewareUrl)) {
      return previousFetch(input, init);
    }

    const enrichedBody = enrichJsonBody(init?.body);
    if (!enrichedBody) {
      return previousFetch(input, init);
    }

    return previousFetch(input, {
      ...init,
      body: enrichedBody,
    });
  };

  window.fetch = fetchWithDeviceMetadata;

  return () => {
    active = false;
    if (window.fetch === fetchWithDeviceMetadata) {
      window.fetch = previousFetch;
    }
  };
}
